'use strict';

import {getQueryStringParams, appendParamsToURL} from '../helpers/urlUtilities';
import * as productUtils from '*/productUtils';

/**
 * Retrieves the product data from the children of the current container.
 * Used by backend to determine what to add to cart.
 *
 * @return {string[]} - List of selected bundle product item ID's
 */
export function getChildProducts(container) {
    let productType = getProductType(container),
        isProductSet = productType === 'set',
        productData = [],
        productList = container.querySelectorAll(`[data-product-container="${productType}-item"]`);

    [].forEach.call(productList, function(currentItem) {
        let currentItemQty = getQuantitySelected(currentItem),
            currentItemData = {
                pid: getPidValue(currentItem),
                options: getOptions(currentItem, true)
            };

        //SFCC checks for different properties depending if item is a Product Set or not (quantity vs qty).
        currentItemData[isProductSet ? 'qty' : 'quantity'] = currentItemQty;

        productData.push(currentItemData);
    });

    return {
        list: productList,
        data: JSON.stringify(productData)
    };
}

/**
 * Retrieve product options
 *
 * @param {HTMLNodeEl} referenceEl - DOM element to use as a reference to find the closest productConatiner.
 * @return {string} - Product options and their selected values
 */
export function getOptions(referenceEl, stringify) {
    let productContainer = getProductContainer(referenceEl),
        options = [].map.call(productContainer.querySelectorAll('[data-option-id]'), function(currentItem) {
            let optionsEl = currentItem.querySelector('[data-product-component="option-selection"]'),
                selectedValueId = optionsEl ? optionsEl.options[optionsEl.selectedIndex].getAttribute('data-value-id') : currentItem.getAttribute('data-option-value-id');

            if (!selectedValueId) {
                [].map.call(productContainer.querySelectorAll('[data-static-option-id]'), function(currentItem) {
                    if (currentItem.classList.contains('selectedStrapOption')) {
                        selectedValueId = currentItem.getAttribute('data-static-option-id');
                    } else if (currentItem.classList.contains('plpSlectedStrap')) {
                        selectedValueId = currentItem.getAttribute('data-static-option-id');
                    }
                });
            }

            return {
                optionId: currentItem.getAttribute('data-option-id'),
                selectedValueId: selectedValueId
            };
        });

    return stringify ? JSON.stringify(options) : options;
}

/**
 * Retrieves the relevant Product ID value.
 */
export function getPidValue(el) {
    return getProductContainer(el).getAttribute('data-pid');
}

/**
 * Retrieves the relevant product type value (pdp, quickview, tile, set, set-item, bundle, bundle-item).
 */
export function getProductType(el) {
    return getProductContainer(el).getAttribute('data-product-container');
}

export function updateSingleID(newID, targetEl, productContainer, updateIDText) {
    //If a `productContainer` is provided, do not update the PIDs for children of sub-products.
    //This is necessary to prevent a product bundle overriding its items PIDs.
    if (!productContainer || productContainer === getProductContainer(targetEl)) {
        targetEl.setAttribute('data-pid', newID);

        if (updateIDText) {
            targetEl.textContent = newID;
        }
    }
}

export function updatePIDs(newID, productContainer) {
    updateSingleID(newID, productContainer);

    [].forEach.call(productContainer.querySelectorAll('[data-pid]'), currentEl => updateSingleID(newID, currentEl, productContainer));
    [].forEach.call(productContainer.querySelectorAll('[data-product-component="pid"]'), currentEl => updateSingleID(newID, currentEl, productContainer, true));
}

/**
 * Update any matches of "[data-product-component="name"]" within a `productContainer` with the `newName` value.
 */
export function updateName(newName, productContainer) {
    [].forEach.call(productContainer.querySelectorAll('[data-product-component="name"]'), currentEl => currentEl.innerHTML = newName);
}

/**
 * Update the product images elements with the provided `responseImages`.
 * @param  {Array} responseImages          [List of images that the view should be updated with]
 * @param  {HTMLElement} productContainer  [The container for the product. Provides context for image selection]
 * @param  {String} imageSelector          [The image selector to use to get a give image list.]
 * @param  {Boolean} updateImageDifference [If TRUE, updates the view so that it matches the amount of images available in `responseImages`]
 */
export function updateImages(responseImages, productContainer, imageSelector = '[data-product-component*="image"]', updateImageDifference) {
    if (updateImageDifference) {
        updateImageList(responseImages, productContainer);
    }

    [].forEach.call(productContainer.querySelectorAll(imageSelector), function(currentEl) {
        updateSingleImage(responseImages[currentEl.getAttribute('data-image-index')], currentEl);
    });
}


/**
 * Checks wether the `responseImages` object matches the current list of viewable images.
 * If it doesn't, updates the view so that viewable images are displayed or hidden.
 */
export function updateImageList(responseImages, productContainer) {
    let imageCarousels = productContainer.querySelectorAll('[data-product-component*="-gallery"]');

    [].forEach.call(imageCarousels, function(imageCarousel) {
        let imageCarouselConfig = imageCarousel.hasAttribute('data-carousel-config') ? $(imageCarousel).slick('getSlick') : null,
            carouselEnabled = imageCarouselConfig && !imageCarouselConfig.unslicked,
            imageCarouselLength = carouselEnabled ? imageCarouselConfig.slideCount : imageCarousel.childElementCount,
            imageCountDifference = (responseImages.length - imageCarouselLength) > 0 ? responseImages.length - imageCarouselLength : 0,
            hasNewImages = responseImages.length > imageCarouselLength;

        //Loop through however many counts of images are either missing or additional.
        for (let i = 0; i < imageCountDifference; i++) {
            //If there are missing images, clone the image template and insert it in the current gallery container.
            if (hasNewImages) {
                let imageTemplate = productContainer.querySelector(`[data-image-template="${imageCarousel.getAttribute('data-product-component')}"]`).cloneNode(true);

                imageTemplate.querySelector('[data-image-index]').setAttribute('data-image-index', imageCarouselLength++);
                imageTemplate.removeAttribute('data-image-template');

                //Either use the native `appendChild` or the carousel's "add" method to insert the new images.
                if (carouselEnabled) {
                    $(imageCarousel).slick('slickAdd', imageTemplate);
                } else {
                    imageCarousel.appendChild(imageTemplate);
                }
            } else {
                //If there are additional images in the DOM, remove them.
                //Check wether or not there's a carousel enabled before removing the elements.
                if (carouselEnabled) {
                    $(imageCarousel).slick('slickRemove', --imageCarouselLength);
                } else {
                    imageCarousel.removeChild(imageCarousel.lastElementChild);
                }
            }
        }

        $('body').trigger('product:imageListUpdate',
            {
                container: productContainer,
                imageContainer: imageCarousel,
                type: imageCarousel.getAttribute('data-product-component'),
            }
        );
    });
}

export function updateSingleImage(currentImgObj, currentImgEl) {
    var isPdpRelatedVariationsProduct = currentImgEl.closest('.pdp-related-variations') != null;
    if (currentImgObj && !isPdpRelatedVariationsProduct) {
        currentImgEl.src = currentImgObj.url;
        currentImgEl.title = currentImgObj.title;
        currentImgEl.alt = currentImgObj.alt;
    }
}

/**
 * Update URLs within the provided `productContainer`.
 * Elements matching the `[data-product-url]` selector will get their `.href` property updated.
 * @param  {[type]} newURL           [The desired new URL]
 * @param  {[type]} productContainer [The target container]
 */
export function updateURLs(newURL, productContainer) {
    let urlEls = productContainer.querySelectorAll('[data-product-url]'),
        queryParams = getQueryStringParams(newURL);

    [].forEach.call(urlEls, function(currentEl) {
        let urlType = currentEl.getAttribute('data-product-url'),
            variationGroup = currentEl.getAttribute('data-product-group'),
            productGrouping = document.querySelector('[data-product-grouping="productGrouping"]'),
            //A URL "type" may be set in the 'data-product-url' attribute.
            //When available, it will look in `window.SFRA.Urls` for a URL type match.
            //This is useful for when you need to update several URLs within a container
            //that may target different Product presentation views, for example a PDP, QuickView, or QuickAdd, etc.
            updatedURL = urlType ? appendParamsToURL(window.SFRA.Urls[urlType], queryParams) : newURL;
        if (currentEl.tagName === 'A') {
            if (productGrouping !== null && variationGroup !== null) {
                currentEl.href = currentEl.href ? currentEl.href : updatedURL;
            } else {
                currentEl.href = updatedURL;
            }
        } else {
            currentEl.setAttribute('data-url', updatedURL);
        }
    });
}

/**
 * Updates matching elements with the new querystring value.
 * This value is used for things like easy requesting data from the current product configuration on demand, like loading a zoom modal.
 * @param  {[type]} newQueryString   [description]
 * @param  {[type]} productContainer [description]
 */
export function updateQueryStrings(newQueryString, productContainer) {
    productContainer.setAttribute('data-product-querystring', newQueryString);
}

/**
 * Updates the current product's price with the provided HTML.
 */
export function updateProductPrice(productContainer, product) {
    let priceEl = productContainer.querySelector('[data-product-component="price"]'),
        priceTaxEl = $('[data-product-component="pdp-price-tax-element"]');
    priceTaxEl.addClass('hidden');

    if (product && priceEl) {
        priceEl.outerHTML = product.price.html;
        if (product.available && product.sellable && (product.price && product.price.type !== 'range' && product.price.sales && product.price.sales.value)) {
            priceTaxEl.removeClass('hidden');
        }
    }
}

/**
 * Retrieve contextual quantity selector
 * @param {jquery} $el - DOM container for the relevant quantity
 * @return {jquery} - quantity selector DOM container
 */
export function getQuantitySelector(el) {
    return getProductContainer(el).querySelector('[data-product-component="qty"]');
}

/**
 * Retrieves the value associated with the Quantity pull-down menu
 * @param {jquery} $el - DOM container for the relevant quantity
 * @return {string} - value found in the quantity input
 */
export function getQuantitySelected(el) {
    let quantitySelector = getQuantitySelector(el);

    return quantitySelector ? quantitySelector.value : 1;
}

export function getProductContainer(el) {
    return el.closest('[data-product-container]');
}

export function chooseColorBackToPdp() {
    let selectedStrapOption = document.querySelector('.selectedStrapOption'),
        defaultStrapOption = document.querySelector('[data-default-strap="true"]'),
        addToCart = document.querySelector('[data-product-component="add-button"]'),
        stockAvailability = document.querySelector('[data-product-component="stock-availability-status"]'),
        availabilityStatus = document.querySelectorAll('[data-product-component="availability-status"]'),
        availabilityNotification = document.querySelector('[data-notification-component="stock-availability-status"]'),
        showNotify = document.querySelector('[data-product-component="stock-availability-status"]'),
        backToProductBtn = document.querySelector('[data-choose-color-back-to-product="back-to-product"]'),
        productInStock = 'true';

    if (backToProductBtn && backToProductBtn != null) {
        productInStock = backToProductBtn.dataset.parentProductInstock;
    }

    if (selectedStrapOption && selectedStrapOption != null) {
        if (selectedStrapOption.dataset.strapInstock === 'true' && productInStock === 'true') {
            if (addToCart && addToCart != null && showNotify && showNotify != null && addToCart.classList.contains('hidden')) {
                addToCart.classList.remove('hidden');
                showNotify.classList.add('hidden');
            }
            if (addToCart.disabled) {
                addToCart.disabled = false;
            }
            if (availabilityNotification && availabilityNotification != null) {
                availabilityNotification.classList.add('hidden');
            }
            if (stockAvailability && stockAvailability != null && availabilityStatus && availabilityStatus != null) {
                stockAvailability.classList.add('hidden');
                [].forEach.call(availabilityStatus, function(currentEl) {
                    currentEl.classList.add('hidden');
                });
            }
        } else {
            if (productInStock === 'false' && addToCart && addToCart != null && showNotify && showNotify != null && showNotify.classList.contains('hidden')) {
                showNotify.classList.remove('hidden');
                addToCart.classList.add('hidden');
            }
            if (stockAvailability && stockAvailability != null && stockAvailability.classList.contains('hidden') && availabilityStatus && availabilityStatus != null) {
                stockAvailability.classList.remove('hidden');
                [].forEach.call(availabilityStatus, function(currentEl) {
                    if ( currentEl.classList.contains('hidden')) {
                        currentEl.classList.remove('hidden');
                    }
                });
            }
            if (availabilityNotification && availabilityNotification != null && availabilityNotification.classList.contains('hidden')) {
                availabilityNotification.classList.remove('hidden');
            }
        }
    } else {
        if (defaultStrapOption && defaultStrapOption != null) {
            if (addToCart.disabled) {
                addToCart.disabled = false;
            }
            if (stockAvailability && stockAvailability != null && availabilityStatus && availabilityStatus != null) {
                stockAvailability.classList.add('hidden');
                [].forEach.call(availabilityStatus, function(currentEl) {
                    currentEl.classList.add('hidden');
                });
            }
            if (availabilityNotification && availabilityNotification != null) {
                availabilityNotification.classList.add('hidden');
            }
            if (addToCart && addToCart != null && showNotify && showNotify != null && defaultStrapOption.dataset.strapInstock === 'true' && productInStock == 'true') {
                if (addToCart.classList.contains('hidden')) {
                    addToCart.classList.remove('hidden');
                    showNotify.classList.add('hidden');
                }
            } else {
                if (productInStock === 'false' && addToCart && addToCart != null && showNotify && showNotify != null && showNotify.classList.contains('hidden')) {
                    showNotify.classList.remove('hidden');
                    addToCart.classList.add('hidden');
                }
                if (stockAvailability && stockAvailability != null && stockAvailability.classList.contains('hidden') && availabilityStatus && availabilityStatus != null ) {
                    stockAvailability.classList.remove('hidden');
                    [].forEach.call(availabilityStatus, function(currentEl) {
                        if ( currentEl.classList.contains('hidden')) {
                            currentEl.classList.remove('hidden');
                        }
                    });
                }
                if (availabilityNotification && availabilityNotification != null && availabilityNotification.classList.contains('hidden')) {
                    availabilityNotification.classList.remove('hidden');
                }
            }
        }
    }
}



$('.product-tile__quickshop-trigger').on('click', function(event) {
    let _ele = $(event.currentTarget),
        _eleProductTile = _ele.parents('.product-tile'),
        _quickshopOptions = _ele.parents('.product-tile__quickshop').find('.product-tile__quickshop-options');

    _eleProductTile.addClass('quick-shop-active');
    _ele.addClass('hidden');
    _quickshopOptions.removeClass('hidden');
})

$('.product-tile__quickshop-options-close-btn').on('click', function(event) {
    let _ele = $(event.currentTarget),
        _eleProductTile = _ele.parents('.product-tile'),
        _eleQuichShopBtn = _ele.parents('.product-tile__quickshop-trigger'),
        _quickshopOptions = _ele.parents('.product-tile__quickshop').find('.product-tile__quickshop-options');

    _eleProductTile.removeClass('quick-shop-active');
    _eleQuichShopBtn.removeClass('hidden');
    _quickshopOptions.addClass('hidden');
})

export var initShareLink = productUtils.initShareLink;
